import type {
  StartMessageValidationResult,
  CharacterCountInfo,
  CharacterCountStatus,
} from "../types/start-message-types";
import { START_MESSAGE_CONSTANTS } from "../types/start-message-types";

/**
 * Validates start message content
 */
export function validateStartMessageContent(
  content: string
): StartMessageValidationResult {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Check if content is empty
  if (!content.trim()) {
    errors.push("Message content cannot be empty");
  }

  // Check character limit
  const characterCount = content.length;
  const remainingCharacters =
    START_MESSAGE_CONSTANTS.EFFECTIVE_MAX_LENGTH - characterCount;

  if (characterCount > START_MESSAGE_CONSTANTS.EFFECTIVE_MAX_LENGTH) {
    errors.push(
      `Message too long. Maximum ${START_MESSAGE_CONSTANTS.EFFECTIVE_MAX_LENGTH} characters allowed (${START_MESSAGE_CONSTANTS.TRACKING_DATA_LENGTH} reserved for tracking)`
    );
  }

  // Warning at 90% capacity
  const warningThreshold =
    START_MESSAGE_CONSTANTS.EFFECTIVE_MAX_LENGTH *
    START_MESSAGE_CONSTANTS.WARNING_THRESHOLD;

  if (
    characterCount > warningThreshold &&
    characterCount <= START_MESSAGE_CONSTANTS.EFFECTIVE_MAX_LENGTH
  ) {
    warnings.push("Message approaching character limit");
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    characterCount,
    remainingCharacters,
  };
}

/**
 * Gets character count information with status
 */
export function getCharacterCountInfo(content: string): CharacterCountInfo {
  const count = content.length;
  const remaining = START_MESSAGE_CONSTANTS.EFFECTIVE_MAX_LENGTH - count;
  const percentage =
    (count / START_MESSAGE_CONSTANTS.EFFECTIVE_MAX_LENGTH) * 100;

  let status: CharacterCountStatus = "safe";
  if (count > START_MESSAGE_CONSTANTS.EFFECTIVE_MAX_LENGTH) {
    status = "error";
  } else if (percentage >= START_MESSAGE_CONSTANTS.WARNING_THRESHOLD * 100) {
    status = "warning";
  }

  return {
    count,
    remaining,
    status,
    percentage,
  };
}

/**
 * Validates start message name
 */
export function validateStartMessageName(name: string): string[] {
  const errors: string[] = [];

  if (!name.trim()) {
    errors.push("Message name is required");
  }

  if (name.length > START_MESSAGE_CONSTANTS.MAX_NAME_LENGTH) {
    errors.push(
      `Name too long. Maximum ${START_MESSAGE_CONSTANTS.MAX_NAME_LENGTH} characters allowed`
    );
  }

  return errors;
}

/**
 * Validates tags array
 */
export function validateStartMessageTags(tags: string[]): string[] {
  const errors: string[] = [];

  if (tags.length > START_MESSAGE_CONSTANTS.MAX_TAGS) {
    errors.push(
      `Too many tags. Maximum ${START_MESSAGE_CONSTANTS.MAX_TAGS} tags allowed`
    );
  }

  // Check for empty tags
  const emptyTags = tags.filter((tag) => !tag.trim());
  if (emptyTags.length > 0) {
    errors.push("Tags cannot be empty");
  }

  // Check for duplicate tags
  const uniqueTags = new Set(tags.map((tag) => tag.toLowerCase().trim()));
  if (uniqueTags.size !== tags.length) {
    errors.push("Duplicate tags are not allowed");
  }

  return errors;
}

/**
 * Validates complete start message form data
 */
export function validateStartMessageForm(data: {
  name: string;
  content: string;
  tags: string[];
}): {
  isValid: boolean;
  errors: Record<string, string[]>;
  warnings: Record<string, string[]>;
} {
  const errors: Record<string, string[]> = {};
  const warnings: Record<string, string[]> = {};

  // Validate name
  const nameErrors = validateStartMessageName(data.name);
  if (nameErrors.length > 0) {
    errors.name = nameErrors;
  }

  // Validate content
  const contentValidation = validateStartMessageContent(data.content);
  if (contentValidation.errors.length > 0) {
    errors.content = contentValidation.errors;
  }
  if (contentValidation.warnings.length > 0) {
    warnings.content = contentValidation.warnings;
  }

  // Validate tags
  const tagErrors = validateStartMessageTags(data.tags);
  if (tagErrors.length > 0) {
    errors.tags = tagErrors;
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
    warnings,
  };
}

/**
 * Generates preview content for SMS and chat formats with realistic tracking data
 */
export function generateStartMessagePreview(
  content: string,
  options: {
    sampleUserId?: string;
    sampleConversationId?: string;
    includeAnalytics?: boolean;
  } = {}
): {
  smsFormat: string;
  chatFormat: string;
  trackingData: string;
  trackingLink: string;
  fullContent: string;
  analytics: {
    contentLength: number;
    trackingDataLength: number;
    trackingLinkLength: number;
    totalWithData: number;
    totalWithLink: number;
    fitsInSMS: boolean;
    exceedsLimit: boolean;
  };
} {
  const {
    sampleUserId = "user123",
    sampleConversationId = "conv456",
    includeAnalytics = true,
  } = options;

  // Generate realistic tracking data
  const trackingData = generateSampleTrackingData(
    sampleUserId,
    sampleConversationId
  );
  const trackingLink = `https://track.activqms.com/t?t=${trackingData}&conversation=${sampleConversationId}&source=start_message`;

  // Create full content with tracking data
  const fullContent = content.trim() ? `${content} ${trackingData}` : "";

  // SMS format includes the full tracking link
  const smsFormat = content.trim() ? `${content} ${trackingLink}` : "";

  // Chat format shows just the message (tracking is in metadata)
  const chatFormat = content;

  // Calculate analytics
  const analytics = includeAnalytics
    ? calculateMessageAnalytics(content, trackingData, trackingLink)
    : {
        contentLength: 0,
        trackingDataLength: 0,
        trackingLinkLength: 0,
        totalWithData: 0,
        totalWithLink: 0,
        fitsInSMS: true,
        exceedsLimit: false,
      };

  return {
    smsFormat,
    chatFormat,
    trackingData,
    trackingLink,
    fullContent,
    analytics,
  };
}

/**
 * Generate sample tracking data in the format {shortUserId}-{shortConversationId}
 */
function generateSampleTrackingData(
  userId: string,
  conversationId: string
): string {
  // Generate 8-character hash-like IDs for realistic preview
  const shortUserId = generateShortId(userId);
  const shortConversationId = generateShortId(conversationId);

  return `${shortUserId}-${shortConversationId}`;
}

/**
 * Generate a short ID from a full ID (simplified version for preview)
 */
function generateShortId(fullId: string): string {
  // Simple hash-like generation for preview purposes
  let hash = 0;
  for (let i = 0; i < fullId.length; i++) {
    const char = fullId.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }

  // Convert to base36 and take first 8 characters
  const shortId = Math.abs(hash).toString(36).padStart(8, "0").substring(0, 8);
  return shortId;
}

/**
 * Calculate message analytics for preview
 */
function calculateMessageAnalytics(
  content: string,
  trackingData: string,
  trackingLink: string
): {
  contentLength: number;
  trackingDataLength: number;
  trackingLinkLength: number;
  totalWithData: number;
  totalWithLink: number;
  fitsInSMS: boolean;
  exceedsLimit: boolean;
} {
  const contentLength = content.length;
  const trackingDataLength = trackingData.length;
  const trackingLinkLength = trackingLink.length;

  // Calculate total lengths (including space separator)
  const totalWithData =
    contentLength + (contentLength > 0 ? 1 : 0) + trackingDataLength;
  const totalWithLink =
    contentLength + (contentLength > 0 ? 1 : 0) + trackingLinkLength;

  // Check SMS limits
  const fitsInSMS = totalWithLink <= 160;
  const exceedsLimit =
    totalWithData > START_MESSAGE_CONSTANTS.EFFECTIVE_MAX_LENGTH;

  return {
    contentLength,
    trackingDataLength,
    trackingLinkLength,
    totalWithData,
    totalWithLink,
    fitsInSMS,
    exceedsLimit,
  };
}

/**
 * Generate preview with real-time updates for form input
 */
export function generateRealTimePreview(
  content: string,
  options: {
    conversationName?: string;
    userName?: string;
    showAnalytics?: boolean;
  } = {}
): {
  preview: ReturnType<typeof generateStartMessagePreview>;
  validation: StartMessageValidationResult;
  recommendations: string[];
} {
  const {
    conversationName = "Sample Conversation",
    userName = "Sample User",
    showAnalytics = true,
  } = options;

  // Generate preview
  const preview = generateStartMessagePreview(content, {
    sampleUserId: userName.toLowerCase().replace(/\s+/g, ""),
    sampleConversationId: conversationName.toLowerCase().replace(/\s+/g, ""),
    includeAnalytics: showAnalytics,
  });

  // Get validation results
  const validation = validateStartMessageContent(content);

  // Generate recommendations
  const recommendations = generatePreviewRecommendations(
    content,
    preview.analytics
  );

  return {
    preview,
    validation,
    recommendations,
  };
}

/**
 * Generate recommendations based on message content and analytics
 */
function generatePreviewRecommendations(
  content: string,
  analytics: ReturnType<typeof calculateMessageAnalytics>
): string[] {
  const recommendations: string[] = [];

  if (!content.trim()) {
    recommendations.push("Add message content to see preview");
    return recommendations;
  }

  if (analytics.exceedsLimit) {
    recommendations.push(
      `Message is too long. Reduce by ${
        analytics.totalWithData - START_MESSAGE_CONSTANTS.EFFECTIVE_MAX_LENGTH
      } characters.`
    );
  }

  if (!analytics.fitsInSMS) {
    recommendations.push(
      "SMS format may be truncated. Consider shortening the message for better SMS compatibility."
    );
  }

  if (analytics.contentLength < 20) {
    recommendations.push(
      "Consider adding more context to make the message more engaging."
    );
  }

  if (
    analytics.contentLength >
    START_MESSAGE_CONSTANTS.EFFECTIVE_MAX_LENGTH * 0.9
  ) {
    recommendations.push(
      "Message is near the character limit. Consider shortening for safety."
    );
  }

  if (!content.includes("?") && !content.includes("!") && content.length > 30) {
    recommendations.push(
      "Consider adding a question or call-to-action to encourage engagement."
    );
  }

  return recommendations;
}
